import { FieldEditChangedArgs, FieldMetadata, FieldMetadataUtil, FieldWidthEnum, RenderCell, UiCommandRenderCell } from 'src/cer/cer-data/cer-data-types';
import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView, ICerViewComponent } from '@form/cer-form-view';
import { WarehouseOnhandClient, WarehouseOnhandCostJournalLineDto } from '@app/api';
import { WarehouseOnhandCostJournalLineForm } from './warehouse-onhand-cost-journal-line.form';
import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { uiCmdSeparator } from '@cer/cer-ui/cer-ui-commands';
import { CerFormPaneSettings, CerFormPanesSettingsUtil } from '@cer/cer-form/cer-form-panes/cer-form-panes.service';
import { CerIntlService } from '@cer/cer-ui/cer-ui-intl.service';

export const uiCmdCreateLines: UiCommand = { id: 'uiCmdCreateLines', text: 'Opret linjer', needSelection: true, toolbarPosition: 'last', target: ".e-content" };
export const uiCmdDeleteLines: UiCommand = { id: 'uiCmdDeleteLines', text: 'Slet linjer', needSelection: true, toolbarPosition: 'last', target: ".e-content" };
export const uiCmdRecalcLines: UiCommand = { id: 'uiCmdRecalcLines', text: 'Genberegn linjer', needSelection: true, toolbarPosition: 'last', target: ".e-content" };
export const uiCmdPostJournal: UiCommand = { id: 'uiCmdPostJournal', text: 'Bogfør linjer', needSelection: true, toolbarPosition: 'last', target: ".e-content" };

export class WarehouseOnhandCostJournalForm extends CerFormAdapter {

  private intl: CerIntlService;

  override viewSetInit(viewSet: CerFormViewSet) {

    this.intl = this.context.injector.get(CerIntlService);

    viewSet.panes = CerFormPanesSettingsUtil.masterDetail('vertical', true, 60, null, 20);
    viewSet.panes.right = new CerFormPaneSettings();
    viewSet.panes.right.visible = 'hide';
    viewSet.panes.right.size = '80%';
    const view = WarehouseOnhandCostJournalForm.viewGet();
    view.onCommandEvent = (e => this.onCommand(e));
    viewSet.viewAdd(view);
    const viewLines = this.viewLineGet();
    viewSet.viewAdd(viewLines, view);
  }

  //#region Journal view
  public static viewGet() {
    return new CerFormView({
      viewMetadata: {
        name: 'standard-kostpris-kladder', dataApiName: 'WarehouseOnhandCostJournalView', text: 'Standardkost kladder',
        primaryKey: 'id', allowEdit: false, allowCreate: true, allowDelete: true,
        baseFields: ['history'], titleFields: ['transDate']
      },

      fieldMetadata: [
        { name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', format: 'Number', widthType: FieldWidthEnum.XS, visible: false },
        { name: 'transDate', text: 'Dato', format: 'DateShort', required: true, orderBy: 'Descending', orderByPriority: 1 },
        { name: 'extraordinary', text: 'Ekstraordinær?', tooltipText: 'Er kladde ekstraordinær?', format: 'CheckBox', defaultValue: false, allowEdit: true, visibleAdd: true, visibleEdit: false },
        { name: 'posted', text: 'Bogført?', tooltipText: 'Er kladde bogført?', format: 'CheckBox', defaultValue: false, allowEdit: false, visibleAdd: false, visibleEdit: false },
        { name: 'voucherNum', text: 'Bilagsnr.', allowEdit: false, visibleAdd: false, visibleEdit: false, visible: false },
        { name: 'cancelled', text: 'Annulleret?', format: 'CheckBox', defaultValue: false, visible: false, allowEdit: false, visibleAdd: false },
        { name: 'lines', text: 'Titler', format: 'Number', allowEdit: false, visibleAdd: false },
        { name: 'adjusted', text: 'Titler auto reguleret', format: 'Number', allowEdit: false, visibleAdd: false },
        { name: 'adjustedUser', text: 'Titler korrigeret', format: 'Number', allowEdit: false, visibleAdd: false },
        { name: 'qty', text: 'Lager stk', format: 'Number', allowEdit: false, visibleAdd: false },
        { name: 'onhandAmountPrevious', text: 'Gl. lagerværdi', format: 'Amount', allowEdit: false, visibleAdd: false, visible: false },
        { name: 'onhandAmountPosted', text: 'Lagerværdi', format: 'Amount', allowEdit: false, visibleAdd: false },
        { name: 'onhandAmountPostedAdjustment', text: 'Regulering', format: 'Amount', allowEdit: false, visibleAdd: false, visible: false },
      ],
      toolbarCommands: [uiCmdCreateLines, uiCmdPostJournal],
      contextMenuCommands: [uiCmdCreateLines, uiCmdPostJournal, uiCmdSeparator, uiCmdDeleteLines, uiCmdRecalcLines]
    });
  }

  private onCommand(e: UiCommandEvent): void {
    if (e.actionType == 'rowSelect' && e.source == 'actionComplete') {
      this.onJournalSelected(e);
    }
    if (e.actionType == 'rowDoubleClick') {
      this.activateViewLines(e);
    }
    if (e.commandId == uiCmdCreateLines.id) {
      this.warehouseClientCall(e.appComponent, e.rowData?.id, uiCmdCreateLines.text, true, false);
    }
    if (e.commandId == uiCmdDeleteLines.id) {
      this.warehouseClientCall(e.appComponent, e.rowData?.id, uiCmdDeleteLines.text, false, false, true);
    }
    if (e.commandId == uiCmdRecalcLines.id) {
      this.warehouseClientCall(e.appComponent, e.rowData?.id, uiCmdRecalcLines.text, false, false, false, true);
    }
    if (e.commandId == uiCmdPostJournal.id) {
      this.context.ui.confirm("Skal kladden bogføres?", "Bogfør kladde", { defaultAction: 'cancel' }).then(action => {
        if (action == 'ok') {
          this.warehouseClientCall(e.appComponent, e.rowData?.id, uiCmdPostJournal.text, false, true);
        }
      });
    }
  }

  private activateViewLines(e: UiCommandEvent) {
    e.cancel = true;
    e.appComponent.rowSelectByIdx(e.args.rowIndex);
    this.routeOptions.context.form.formContextService.activate(this.viewLine);
  }

  private isJournalPosted: boolean;
  private journalId: number;
  private extraOrdinary: boolean;
  private transDate: Date;

  private onJournalSelected(e: UiCommandEvent) {
    const jour = e.rowData;
    this.isJournalPosted = jour.posted;
    this.journalId = jour.id;
    this.extraOrdinary = jour.extraOrdinary;
    this.transDate = jour.transDate;

    this.journalEnableByPosted(e);
  }

  private journalEnableByPosted(e: UiCommandEvent) {
    const grid: CerGridComponent = e.appComponent as CerGridComponent;
    if (grid) {
      e.appComponent.editService.allowDelete(!this.isJournalPosted);
      grid.commandService.commandVisible(uiCmdCreateLines.id, !this.isJournalPosted);
      grid.commandService.commandVisible(uiCmdPostJournal.id, !this.isJournalPosted);
    }
  }
  //#endregion Journal view

  //#region API
  private warehouseJournalClient: WarehouseOnhandClient;
  private journalClient(): WarehouseOnhandClient {
    this.warehouseJournalClient ??= this.context.injector.get(WarehouseOnhandClient);
    return this.warehouseJournalClient;
  }

  private journalLineDtoGet(journalId: number, productNum: string, projectNum: string): Promise<WarehouseOnhandCostJournalLineDto> {
    return new Promise<WarehouseOnhandCostJournalLineDto>(resolve => {
      this.journalClient().costJournalLineGet(journalId, productNum, projectNum).subscribe({
        next: dto => resolve(dto),
        error: error => {
          this.context.ui.errorApi(error);
          resolve(null);
        }
      });
    });
  }

  private warehouseClientCall(c: ICerViewComponent, id: number,
    title: string,
    createLines: boolean,
    post: boolean,
    deleteLines: boolean = false,
    recalcLines: boolean = false
  ) {
    if (id != null) {
      this.context.ui.operationBegin("Udfører", title)
      if (createLines) {
        this.journalClient().costJournalLinesCreate(id).subscribe({
          next: _ => {
            this.context.ui.operationEnd("Fuldført");
            c.research();
          },
          error: error => this.context.ui.operationEndError(error)
        });
      }
      else if (post) {
        this.journalClient().costJournalLinesPost(id).subscribe({
          next: _ => {
            this.context.ui.operationEnd("Fuldført");
            c.research();
          },
          error: error => this.context.ui.operationEndError(error)
        });
      }
      else if (deleteLines) {
        this.journalClient().costJournalLinesDelete(id).subscribe({
          next: _ => {
            this.context.ui.operationEnd("Fuldført");
            c.research();
          },
          error: error => this.context.ui.operationEndError(error)
        });
      }
      else if (recalcLines) {
        this.journalClient().costJournalLinesRecalc(id).subscribe({
          next: _ => {
            this.context.ui.operationEnd("Fuldført");
            c.research();
          },
          error: error => this.context.ui.operationEndError(error)
        });
      }
    }
  }
  //#endregion

  //#region Journal lines View
  private viewLine: CerFormView;
  private viewLineGet(): CerFormView {
    const v = WarehouseOnhandCostJournalLineForm.viewGet(true);
    this.viewLine = v;
    v.onCommandEvent = (e => this.onLineCommand(e));
    v.mandatory = true;

    v.viewMetadata.parentForeignKey = 'journal';
    v.viewMetadata.parentPrimaryKey = 'id';
    v.viewMetadata.allowEdit = true; // default
    v.viewMetadata.allowDelete = true; // default
    v.viewMetadata.allowCreate = true; // default
    v.viewMetadata.navigations ??= [];
    v.viewMetadata.navigations.unshift(
      { link: 'nav', title: 'Historik', paneTarget: 'right', foreignKey: { parentEntity: 'WarehouseOnhandCostJournalLineView', parentColumn: 'productNum', column: 'productNum' } }
    );

    v.title = 'Titler, kostpris';
    v.listPaging = 'pager';
    //v.listSelectionMode = 'cellBox';
    v.listEditSettings = { mode: 'inline', editOnClick: true, editConfirm: false, editBatchCancelConfirm: true };

    this.lineViewFieldsSetup(v.fieldMetadata);

    return v;
  }

  private onLineCommand(e: UiCommandEvent): void {
    if (e.source == 'actionComplete') {
      if (e.actionType == 'rowSelect') {
        if (e.columnName?.length > 0) {
          const f = FieldMetadataUtil.fieldByName(e.columnName, e.viewMetadata?.fieldMetaData);
          if (f?.allowEdit) {
            const grid: CerGridComponent = e.appComponent as CerGridComponent;
            const rowIndex = e.args?.rowIndex;
            grid?.editService.doEditStart(rowIndex, f.name);
          }
        }
      }
      if (e.actionType == 'refreshFromParent') {
        //this.lineEnableByPosted(e.appComponent as CerGridComponent);
      }
      if (e.actionType == 'dataBound') {
        this.lineEnableByPosted(e.appComponent as CerGridComponent);
      }
    }
    if (e.source == 'actionBegin') {
      if (['beginEdit', 'add'].includes(e.actionType)) {
        if (this.isJournalPosted === true) {
          e.cancel = true;
        }
      }
      if (e.actionType == 'add') {
        this.lineInitFromJournal(e);
      }
    }
  }

  private lineInitFromJournal(e: UiCommandEvent) {
    const defaultData: any = (<any>e.args).defaultData;
    if (defaultData != null) {

      defaultData.journal = this.journalId;
      defaultData.isExtraordinary = this.extraOrdinary;
      defaultData.transDate = this.transDate;
    }
  }

  private lineEnableByPosted(grid: CerGridComponent) {
    if (grid) {
      const edit = this.isJournalPosted === false;
      const addDel = edit /*&& this.isJournalExtraordinary === true*/;

      //grid.editService.editMode(edit ? 'Normal' : 'Dialog')
      grid.editService.allowEdit(edit);
      grid.editService.allowAdd(addDel);
      grid.editService.allowDelete(addDel);
      grid.selection.initGridSelectionSettings();
    }
  }
  //#endregion

  //#region Journal line fields
  private lineViewFieldsSetup(f: FieldMetadata[]) {

    f.forEach(f => { f.allowEdit = false; f.allowEditOnAdd = false; });

    FieldMetadataUtil.mergeByName('productNum', f, {
      allowEdit: false,
      allowEditOnAdd: true,
      foreignKeyType: 'product',
      onEditChanged: (e) => { this.lineProductChanged(e) }
    });

    FieldMetadataUtil.mergeByName('projectNum', f, {
      allowEdit: false,
      allowEditOnAdd: true,
      foreignKeyType: 'productProject',
      foreignKeyValue: 'projectNum',
      foreignKeyField: 'projectNum',
      onEditChanged: (e) => { this.lineProductChanged(e); }
    });

    FieldMetadataUtil.mergeByName('costPriceUser', f, {
      allowEdit: true,
      allowEditOnAdd: true,
      allowClear: true,
      onRenderCell: (e) => { lineCostPriceUserRender(e); },
      onEditChanged: (e) => { lineCostPriceUserChanged(e); }
    });

    FieldMetadataUtil.mergeByName('costPricePrevious', f, {
      onRenderCell: (e) => { lineCostPricePreviousRender(e) },
      onExcelCell: (e) => { lineCostPricePreviousRender(e) }
    });

    FieldMetadataUtil.mergeByName('costPriceCalc', f, {
      onRenderCell: (e) => { this.lineCostPriceCalcRender(e); },
      //onExcelCell: (e) => { this.lineCostPriceCalcRender(e); }
    });

    FieldMetadataUtil.mergeByName('costPriceCalcAmount', f, {
      onRenderCell: (e) => { this.lineCostPriceCalcAmountRender(e); },
      //onExcelCell: (e) => { this.lineCostPriceCalcAmountRender(e); }
    });

    FieldMetadataUtil.mergeByName('costPriceCalcQty', f, {
      onRenderCell: (e) => { this.linePurchQty(e); },
      //onExcelCell: (e) => { this.linePurchQty(e); }
    });
  }

  private async lineProductChanged(e: FieldEditChangedArgs) {
    const row = e.data;
    const lookupLine = e.itemData;
    if (row && lookupLine) {
      const journalId = row.journal;
      if (lookupLine.projectNum?.length > 0 || lookupLine.num?.length > 0) {
        if (lookupLine.num?.length > 0 && lookupLine.num == row.productNum) {
          return;
        }
        const lineDto = await this.journalLineDtoGet(journalId, lookupLine.num, lookupLine.projectNum);
        if (lineDto != null && row != null) {
          if (lineDto.id == 0) {
            lineDto.id = undefined;
          }
          e.editService.valueSet(row, 'productNum', lookupLine.num);
          e.editService.valueSet(row, 'projectNum', lookupLine.projectNum);
          e.editService.valueSet(row, 'name', lookupLine.name);
          e.editService.mergeProperties(row, lineDto, true);
        }
        else {
          this.context.ui.error(
            [
              'Kan ikke finde beholdning for titel:',
              `ISBN: ${lookupLine.productNum}`,
              `Projekt: ${lookupLine.projectNum}`,
              `Titel: ${lookupLine.name}`
            ],
            'Valideringsfejl');
          return;
        }
      }
    }
  }

  private lineCostPriceCalcRender(e: UiCommandRenderCell) {
    if (!e.data) {
      return;
    }
    const intl = this.intl;
    const line: WarehouseOnhandCostJournalLineDto = <any>e.data;
    if (line?.costPriceCalc != null) {
      
      var c = `${intl.formatDecimal(line.costPriceCalcAmount)} : ${intl.formatNumber(line.costPriceCalcQty)} = ${intl.formatDecimal(line.costPriceCalcPrice)}`;
     
      if (line.purchQty != null && line.purchQty != line.costPriceCalcQty) {
        RenderCell.backgroundColor(e, 'yellow');
        c += `<br/>Total tilgang: ${intl.formatNumber(line.purchQty)}`
      }
      if (line.costAmount != null && line.costAmount != line.costPriceCalcAmount) {
        RenderCell.backgroundColor(e, 'yellow');
        c += `<br/>Total omkostning: ${intl.formatDecimal(line.costAmount)}`
      }
      RenderCell.createTooltip(e, c);
    }
  }

  private lineCostPriceCalcAmountRender(e: UiCommandRenderCell) {
    const line: WarehouseOnhandCostJournalLineDto = e.data as WarehouseOnhandCostJournalLineDto;
    if (line?.costPriceCalc != null) {
      if (line.costAmount != line.costPriceCalcAmount && line.costPriceCalcAmount != null && line.costAmount != null) {
        RenderCell.backgroundColor(e, 'yellow');
        const c = `Total omkostning: ${this.intl.formatDecimal(line.costAmount)}`;
        RenderCell.createTooltip(e, c);
      }
    }
  }

  private linePurchQty(e: UiCommandRenderCell) {
    const line: WarehouseOnhandCostJournalLineDto = e.data as WarehouseOnhandCostJournalLineDto;
    if (line?.costPriceCalc != null) {
      if (line.purchQty != line.costPriceCalcQty && line.costPriceCalcQty != null && line.purchQty != null) {
        RenderCell.backgroundColor(e, 'yellow');
        const c = `Total tilgang: ${this.intl.formatNumber(line.purchQty)}`
        RenderCell.createTooltip(e, c);
      }
    }
  }
  //#endregion
}

function lineCostPricePreviousRender(e: UiCommandRenderCell) {
  if (!e.data) {
    return;
  }
  const line: WarehouseOnhandCostJournalLineDto = e.data;
  if (line.isExtraordinaryCostPricePrevious == true && e.value != null) {
    RenderCell.backgroundColor(e, 'red');
  }
}

function lineCostPriceUserRender(e: UiCommandRenderCell) {
  if (!e.data || (<any>e.data).posted == true) {
    return;
  }
  const value = FieldMetadataUtil.valueGet<number>(e.data, FieldMetadataUtil.fieldMetaByColumn(e.column));
  if (value != null && value != 0) {
    RenderCell.elementBackgroundColorHighLight(e.cell);
  }
  else {
    RenderCell.elementBackgroundColorHighLightAlt(e.cell);
  }
}

function lineCostPriceUserChanged(e: FieldEditChangedArgs) {
  const row: WarehouseOnhandCostJournalLineDto = e.data;
  if (row) {
    var costBase = row.costPriceCalc ?? row.costPricePrevious;
    costBase = row.costPriceAge != null && row.costPriceAge < (costBase ?? 0) ? row.costPriceAge : costBase;
    row.costPricePosted = row.costPriceUser ?? costBase;
    row.costPricePostedAdjustment = row.costPricePosted == null ? null : (row.costPricePosted - (row.costPricePrevious ?? 0));
    row.onhandAmountPosted = row.costPricePosted != null && row.qty != null ? row.costPricePosted * row.qty : null;
    row.onhandAmountPostedAdjustment = row.onhandAmountPosted != null ? row.onhandAmountPosted - (row.onhandAmountPrevious ?? 0) : null;
    e.editService.valueSet(row, 'costPricePosted', row.costPricePosted);
    e.editService.valueSet(row, 'costPricePostedAdjustment', row.costPricePostedAdjustment);
    e.editService.valueSet(row, 'costPricePosted', row.costPricePosted);
    e.editService.valueSet(row, 'onhandAmountPosted', row.onhandAmountPosted);
    e.editService.valueSet(row, 'onhandAmountPostedAdjustment', row.onhandAmountPostedAdjustment);
  }
}

